import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import Drawer from 'components/Drawer'
import Button from 'components/Button'
import { Radio } from 'antd'
import TextEditor from 'components/TextEditor'

const Section = styled.div`
  margin-bottom: 24px;
`

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 8px;
`

const InstructionsContent = styled.div`
  display: ${props => (props.isEditMode ? 'none' : 'block')};
  margin-top: 16px;
  padding: 16px;
  background: #ffffff;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
`

const EditorContainer = styled.div`
  display: ${props => (props.isEditMode ? 'block' : 'none')};
  margin-top: 16px;
  margin-bottom: 16px;
  position: relative;
  padding-top: 48px;

  .ql-toolbar {
    border-radius: 8px 8px 0 0;
  }
  .ql-container {
    border-radius: 0 0 8px 8px;
    min-height: 200px;
    max-height: 400px;
  }
`

const RadioGroup = styled(Radio.Group)`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`

const StyledRadio = styled(Radio)`
  padding: 8px 16px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  margin-right: 0 !important;
`

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  justify-content: space-between;
`

const EditButton = styled.button`
  height: 40px;
  padding: 8px 16px;
  background: white;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  &:hover {
    background: #f9fafb;
  }
`

const InstructionsDrawer = ({
  visible,
  onClose,
  onEdit,
  closeEdit,
  onSave,
  isEditMode,
  initialInstructions,
  initialAllowProctoring,
  initialAllowVerification,
  assessmentForms,
  currentEditingIndex,
  entityType,
  assessmentType,
}) => {
  // Use string values for Radio to avoid Ant Design boolean-to-string coercion
  const [proctoringChoice, setProctoringChoice] = useState(
    initialAllowProctoring === false ? 'disabled' : 'enabled'
  )
  const [verificationChoice, setVerificationChoice] = useState(
    initialAllowVerification === false ? 'disabled' : 'enabled'
  )
  const [instructions, setInstructions] = useState('')
  const [editorKey, setEditorKey] = useState(0)
  const [tempInstructions, setTempInstructions] = useState('')
  const prevVisibleRef = useRef(false)

  // Only sync proctoring state when drawer first opens (false -> true transition)
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      setProctoringChoice(initialAllowProctoring === false ? 'disabled' : 'enabled')
      setVerificationChoice(initialAllowVerification === false ? 'disabled' : 'enabled')
    }
    prevVisibleRef.current = visible
  }, [visible, initialAllowProctoring, initialAllowVerification])

  useEffect(() => {
    let startingInstructions = initialInstructions
    if (!startingInstructions) {
      if (assessmentType === 'Aptitude') {
        startingInstructions = aptitudeInstructions
      } else if (assessmentType === 'Hinglish') {
        startingInstructions = hinglishInstructions
      } else if (assessmentType === 'Role_Based') {
        startingInstructions = roleBasedInstructions
      } else if (assessmentType === 'Custom_Assessment') {
        startingInstructions = customAssessmentInstructions
      } else {
        startingInstructions = communicationInstructions
      }
    }
    setInstructions(startingInstructions)
    setTempInstructions(startingInstructions)
    setEditorKey(prev => prev + 1)
  }, [initialInstructions, assessmentType])

  const handleSaveInstructions = newInstructions => {
    setTempInstructions(newInstructions)
    setInstructions(newInstructions) // Update visible instructions
    closeEdit()
  }

  const handleFinalSave = () => {
    const proctoringValue = proctoringChoice === 'enabled'
    const verificationValue = verificationChoice === 'enabled'
    onSave(tempInstructions, proctoringValue, verificationValue)
  }

  const communicationInstructions = `
  <div style="font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #242E39; line-height: 24px;">
    <div style="font-weight: 500;">For your assessments, follow these instructions carefully. You will have an overall timer running for 5 minutes.</div>
    <div style="font-weight: 500;">Use your time wisely across the three tasks:</div>
    <br/>
    <div style="font-weight: 600; color: #161C22;">Language Verbal Ability - Video Response</div>
    <div style="color: #242E39;">Objective: Assess English Language Verbal Ability.</div>
    <div style="color: #242E39;">What to do:</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Record a short video response to one question (e.g., introducing yourself or speaking on a topic).</li>
      <li>Ensure your response is clear, well-structured, and concise.</li>
    </ul>
    <br/>
    <div style="font-weight: 600; color: #161C22;">Reading ability/ Comprehension - Reading Comprehension</div>
    <div style="color: #242E39;">Objective: Evaluate Critical Thinking & Logical Thinking.</div>
    <div style="color: #242E39;">What to do:</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Read up to three paragraphs (around 300 words total).</li>
      <li>For each paragraph, answer three aptitude-style questions related to critical or logical thinking.</li>
    </ul>
    <br/>
    <div style="font-weight: 600; color: #161C22;">Listening ability - Audio Questions</div>
    <div style="color: #242E39;">Objective: Assess Reading Ability & Comprehension.</div>
    <div style="color: #242E39;">What to do:</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Read aloud paragraphs (total 300 words each).</li>
      <li>Answer three Multiple Choice Questions (MCQs) based on paragraph</li>
    </ul>
    <br/>
    <div style="font-weight: 500; color: #242E39;">Remember to keep an eye on the overall timer, and manage your time effectively to complete all tasks within the allocated duration.</div>
  </div>
`

  const aptitudeInstructions = `
  <div style="font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #242E39; line-height: 24px;">
    <div style="font-weight: 500;">For your Aptitude assessment, please follow these instructions carefully.</div>
    <br/>
    <div style="font-weight: 600; color: #161C22;">Aptitude Test Structure</div>
    <div style="color: #242E39;">Objective: Assess Quantitative, Logical, and Critical Reasoning skills.</div>
    <div style="color: #242E39;">What to do:</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Answer multiple-choice questions across Quantitative, Logical Reasoning, and Critical Reasoning sections.</li>
      <li>Read each question carefully and select the most appropriate answer.</li>
      <li>Manage your time effectively to attempt all questions within the allotted duration.</li>
    </ul>
    <br/>
    <div style="font-weight: 500; color: #242E39;">Note: The test is auto-configured based on your institute/corporate settings. All questions are mandatory unless specified otherwise.</div>
  </div>
`

  const hinglishInstructions = `
  <div style="font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #242E39; line-height: 24px;">
    <div style="font-weight: 500;">Hinglish Communication Assessment</div>
    <div style="color: #242E39;">This assessment evaluates your ability to communicate in Hinglish (Hindi-English mix) for customer care roles.</div>
    <br/>
    <div style="font-weight: 600; color: #161C22;">Assessment Sections:</div>
    <br/>
    <div style="font-weight: 600; color: #161C22;">1. Paragraph Reading (2.5 minutes)</div>
    <div style="color: #242E39;">Read a Hinglish paragraph aloud.</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Read clearly and at a natural pace.</li>
      <li>Pronunciation and fluency will be evaluated.</li>
    </ul>
    <br/>
    <div style="font-weight: 600; color: #161C22;">2. Audio Questions (2 minutes)</div>
    <div style="color: #242E39;">Listen to Hinglish audio and answer questions.</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Listen carefully to the audio clips.</li>
      <li>Answer the questions based on what you heard.</li>
    </ul>
    <br/>
    <div style="font-weight: 600; color: #161C22;">3. Video Response (5 minutes)</div>
    <div style="color: #242E39;">Respond to prompts in Hinglish via video.</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Speak naturally in Hinglish.</li>
      <li>Ensure your camera and microphone are working properly.</li>
    </ul>
    <br/>
    <div style="font-weight: 600; color: #161C22;">Important:</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Ensure your camera and microphone are working.</li>
      <li>Find a quiet environment.</li>
      <li>Speak naturally in Hinglish.</li>
      <li>The assessment is one-time only.</li>
    </ul>
  </div>
`

  const roleBasedInstructions = `
  <div style="font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #242E39; line-height: 24px;">
    <div style="font-weight: 500;">For your Role-Based assessment, please follow these instructions carefully.</div>
    <br/>
    <div style="font-weight: 600; color: #161C22;">Assessment Structure</div>
    <div style="color: #242E39;">This assessment evaluates your skills across multiple sections:</div>
    <br/>
    <div style="font-weight: 600; color: #161C22;">1. Multiple Choice Questions (MCQ)</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Select the most appropriate answer for each question.</li>
      <li>Questions are based on the role-specific skills and knowledge areas.</li>
    </ul>
    <br/>
    <div style="font-weight: 600; color: #161C22;">2. Subjective Questions</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Provide detailed written responses to scenario-based questions.</li>
      <li>Demonstrate your understanding and problem-solving approach.</li>
    </ul>
    <br/>
    <div style="font-weight: 600; color: #161C22;">3. Video Response</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Record a video response to the given prompt.</li>
      <li>Ensure your camera and microphone are working properly.</li>
      <li>Speak clearly and structure your response.</li>
    </ul>
    <br/>
    <div style="font-weight: 600; color: #161C22;">4. Coding (if applicable)</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Write code to solve the given problem.</li>
      <li>Your code will be evaluated against test cases.</li>
      <li>Focus on correctness, efficiency, and code quality.</li>
    </ul>
    <br/>
    <div style="font-weight: 500; color: #242E39;">Important: Manage your time effectively across all sections. Ensure you are in a quiet environment with a stable internet connection.</div>
  </div>
`

  const customAssessmentInstructions = `
  <div style="font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #242E39; line-height: 24px;">
    <div style="font-weight: 500;">For your Custom assessment, please follow these instructions carefully.</div>
    <br/>
    <div style="font-weight: 600; color: #161C22;">Assessment Structure</div>
    <div style="color: #242E39;">This assessment contains questions from selected sections. Please review each question carefully before answering.</div>
    <br/>
    <div style="font-weight: 600; color: #161C22;">Instructions:</div>
    <ul style="list-style-type: disc; padding-left: 20px; color: #242E39;">
      <li>Read each question carefully and select/provide the most appropriate answer.</li>
      <li>For multiple choice questions, only one answer is correct unless specified otherwise.</li>
      <li>Manage your time effectively to attempt all questions within the allotted duration.</li>
      <li>Once submitted, you cannot go back to change your answers.</li>
    </ul>
    <br/>
    <div style="font-weight: 500; color: #242E39;">Ensure you are in a quiet environment with a stable internet connection before starting the assessment.</div>
  </div>
`

  return (
    <Drawer
      title="Assessment Instructions"
      placement="right"
      width="800px"
      onClose={onClose}
      visible={visible}
      content={
        <>
          <Section>
            <Label>Proctoring</Label>
            <RadioContainer>
              <RadioGroup
                value={proctoringChoice}
                onChange={e => setProctoringChoice(e.target.value)}
              >
                <StyledRadio value="enabled">Enable</StyledRadio>
                <StyledRadio
                  value="disabled"
                  disabled={entityType === 'college'}
                >
                  Disable
                </StyledRadio>
              </RadioGroup>
              {!isEditMode && (
                <EditButton onClick={onEdit}>Edit Instructions</EditButton>
              )}
            </RadioContainer>
          </Section>

          <Section>
            <Label>Biometric Verification</Label>
            <RadioContainer>
              <RadioGroup
                value={verificationChoice}
                onChange={e => setVerificationChoice(e.target.value)}
              >
                <StyledRadio value="enabled">Enable</StyledRadio>
                <StyledRadio value="disabled">Disable</StyledRadio>
              </RadioGroup>
            </RadioContainer>
          </Section>

          <InstructionsContent isEditMode={isEditMode}>
            <div dangerouslySetInnerHTML={{ __html: instructions }} />
          </InstructionsContent>

          <EditorContainer isEditMode={isEditMode}>
            <TextEditor
              key={editorKey}
              initialContent={tempInstructions}
              onSave={handleSaveInstructions}
              onClose={closeEdit}
            />
          </EditorContainer>
        </>
      }
      footer={
        <FooterButtons>
          <Button.Secondary text="Cancel" onClick={onClose} />
          <Button.Primary text={assessmentType === 'Role_Based' ? 'Save & Generate' : 'Save'} onClick={handleFinalSave} />
        </FooterButtons>
      }
    />
  )
}

export default InstructionsDrawer
